import { css, styled } from 'styled-components';
import { BonusShopLabel, BonusShopProductType } from '@/api/cms/bonusShop';
import { hexToRGB, screen, typography } from '@/styles/helpers';

const LABEL_NEW_COLOR = '#0ec277';
const LABEL_SPECIAL_COLOR = '#8a38f5';

const goldGradient = `linear-gradient(
  143deg,
  #d09f12 0%,
  #f0c542 42.6%,
  #f1c641 60%,
  #d09f12 100%
)`;

const goldBorderGradient = `linear-gradient(
  180deg,
  #fbf2ae 0%,
  #a26813 50%,
  #d9aa56 100%
)`;

interface ScBonusShopCardProps {
  $type: BonusShopProductType;
  $hasCta: boolean;
}

export const ScBonusShopCard = styled.div<ScBonusShopCardProps>`
  container: bonus-shop-card / inline-size;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: space-between;
  height: 100%;
  padding: 24px 12px 12px;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.grayDarkColor};
  overflow: hidden;
  min-width: 232px;

  @media (${screen.md}) {
    gap: ${({ $type }) =>
      $type === BonusShopProductType.FREE_SPINS ? 24 : 0}px;
  }

  ${({ $type }) =>
    $type === BonusShopProductType.PREMIUM &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        padding: 1px;
        border-radius: inherit;
        background: ${goldBorderGradient};
        -webkit-mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        pointer-events: none;
      }

      & .product-type,
      & .title {
        background-image: ${goldGradient};
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;
      }
    `}

  & .content {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  & .heading {
    display: flex;
    gap: 8px;
    align-items: center;
    flex: 1;
  }

  & .image {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    object-fit: contain;

    @media (${screen.md}) {
      width: 80px;
      height: 80px;
    }
  }

  & .info {
    display: flex;
    flex: 1 0 0;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    align-items: flex-start;
  }

  & .product-type {
    font-size: 12px;
    line-height: 14px;
  }

  & .title {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
    color: ${({ theme }) => theme.secondaryColor};
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    letter-spacing: 0.14px;
    text-transform: ${({ $type }) =>
      [BonusShopProductType.CASH, BonusShopProductType.CASH_BONUS].includes(
        $type
      )
        ? 'none'
        : 'uppercase'};

    & .type-name {
      text-transform: uppercase;
    }

    @media (${screen.md}) {
      ${typography.headline3}
    }
  }

  & .subtitle {
    color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.6)};
    font-size: 12px;
    line-height: 14px;
  }

  & .divider {
    height: 1px;
    margin-block: 8px;
    background-color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.2)};

    @media (${screen.md}) {
      margin-top: 16px;
      margin-bottom: ${({ $type }) =>
        $type === BonusShopProductType.FREE_SPINS ? 16 : 8}px;
    }
  }

  & .footer {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: ${({ $hasCta }) => ($hasCta ? 'space-between' : 'center')};
  }

  & .price {
    display: flex;
    gap: 4px;
    align-items: center;
    flex-shrink: 0;
    color: ${({ theme }) => theme.whiteColor};
    font-size: 14px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: 0.4px;

    & > img {
      width: 20px;
      height: 20px;
    }

    @media (${screen.md}) {
      font-size: 16px;

      & > img {
        width: 24px;
        height: 24px;
      }
    }
  }

  & .cta {
    min-width: 0;

    & button {
      width: 98px;
    }
  }
`;

interface ScBonusShopCardLabelProps {
  $label: BonusShopLabel;
  $inline?: boolean;
}

export const ScBonusShopCardLabel = styled.span<ScBonusShopCardLabelProps>`
  position: ${({ $inline }) => ($inline ? 'static' : 'absolute')};
  top: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 17px;
  padding: 0 5px;
  border-radius: 20px;
  background-color: ${LABEL_NEW_COLOR};
  color: ${({ theme }) => theme.whiteColor};
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
  text-transform: uppercase;

  ${({ $label, theme }) => {
    if ($label === BonusShopLabel.VIP)
      return css`
        padding: 0 8px;
        border-radius: 360px;
        background-color: transparent;
        background-image: ${goldGradient};
        color: ${theme.blackColor};
      `;

    if ($label === BonusShopLabel.SPECIAL)
      return css`
        background-color: ${LABEL_SPECIAL_COLOR};
      `;
  }}
`;

export const ScBonusShopCardVersions = styled.div`
  display: flex;
  gap: 8px;
`;

export const ScBonusShopCardVersion = styled.button<{ $isActive: boolean }>`
  flex: 1 0 0;
  min-width: 0;
  height: 28px;
  padding: 4px 8px;
  border: 1px solid
    ${({ theme, $isActive }) => ($isActive ? theme.mainColor : theme.grayColor)};
  border-radius: 360px;
  background-color: ${({ theme, $isActive }) =>
    $isActive ? theme.blackColor : 'transparent'};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.whiteColor : theme.blackLightColor};
  ${typography.caption1bold}
  text-transform: uppercase;
  cursor: ${({ $isActive }) => ($isActive ? 'default' : 'pointer')};
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover:not(:disabled) {
    ${({ $isActive, theme }) =>
      !$isActive &&
      `
        border-color: ${theme.mainColorHover};
        color: ${theme.whiteColor};
      `}
  }

  @media (${screen.md}) {
    height: 34px;
    font-size: 14px;
    line-height: 14px;
  }
`;

export const ScBonusShopCardTimer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  background: ${({ theme }) => hexToRGB(theme.blackColor, 0.4)};
  backdrop-filter: blur(5px);
  color: ${({ theme }) => theme.whiteColor};
  font-size: 10px;
  font-weight: 700;
  line-height: normal;
`;
